import { useEffect, useRef, useState } from 'react';
import { api, type LiveTrip } from './api';
import { decodePolyline, loadMaps } from './TripMap';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Google's dark style, close to the console's navy, so the map does not glare. */
const DARK = [
  { elementType: 'geometry', stylers: [{ color: '#0f1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8592ad' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0b1220' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1c2a45' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2a3a5c' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#091020' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

/**
 * Every running trip on one map: its planned line, and the car where it last
 * reported, labelled with its plate — red when it has an open SOS. Redrawn on
 * each poll; the map itself is created once.
 */
export function FleetMap({ trips, dark, onPick }: { trips: LiveTrip[]; dark: boolean; onPick?: (id: string) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const drawn = useRef<any[]>([]);
  const fitted = useRef(false);
  const [state, setState] = useState<'loading' | 'ready' | 'nokey' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    api.config()
      .then(async (cfg) => {
        if (!cfg.mapsBrowserKey) return setState('nokey');
        await loadMaps(cfg.mapsBrowserKey);
        if (cancelled || !host.current) return;
        const g = (window as any).google;
        map.current = new g.maps.Map(host.current, {
          center: { lat: 28.57, lng: 77.36 }, zoom: 11,
          mapTypeControl: false, streetViewControl: false, fullscreenControl: true, clickableIcons: false,
          styles: dark ? DARK : undefined,
        });
        setState('ready');
      })
      .catch(() => setState('error'));
    return () => { cancelled = true; };
  }, [dark]);

  useEffect(() => {
    if (state !== 'ready' || !map.current) return;
    const g = (window as any).google;
    drawn.current.forEach((o) => o.setMap(null));
    drawn.current = [];
    const bounds = new g.maps.LatLngBounds();
    for (const t of trips) {
      if (t.status !== 'started') continue;
      const path = t.polyline ? decodePolyline(t.polyline) : [];
      if (path.length > 1) {
        drawn.current.push(new g.maps.Polyline({ map: map.current, path, strokeColor: t.sos ? '#ff6b5c' : '#5570e8', strokeOpacity: 0.85, strokeWeight: 4 }));
        path.forEach((p) => bounds.extend(p));
      }
      if (t.position) {
        const m = new g.maps.Marker({
          map: map.current,
          position: { lat: t.position.lat, lng: t.position.lng },
          title: `${t.vehicle?.plate ?? ''} · ${t.driver.name ?? ''}`,
          label: t.vehicle ? { text: t.vehicle.plate, color: '#0d1526', fontSize: '10px', fontWeight: '800' } : undefined,
          icon: {
            path: 'M -22,-9 L 22,-9 L 22,9 L -22,9 Z',
            fillColor: t.sos ? '#ff6b5c' : '#ffc400', fillOpacity: 1,
            strokeColor: '#0d1526', strokeWeight: 1.5, scale: 1, labelOrigin: new g.maps.Point(0, 0),
          },
          zIndex: t.sos ? 10 : 1,
        });
        if (onPick) m.addListener('click', () => onPick(t.id));
        drawn.current.push(m);
        bounds.extend({ lat: t.position.lat, lng: t.position.lng });
      }
    }
    if (!fitted.current && !bounds.isEmpty()) {
      map.current.fitBounds(bounds, 40);
      fitted.current = true;
    }
  }, [trips, state, onPick]);

  return (
    <div className="fleetmap">
      <div ref={host} className="fleetmap-canvas" />
      {state !== 'ready' ? (
        <div className="fleetmap-note">
          {state === 'nokey'
            ? 'Set GOOGLE_MAPS_BROWSER_KEY on the server to see the live fleet map.'
            : state === 'error' ? 'The map could not load.' : 'Loading the map…'}
        </div>
      ) : null}
    </div>
  );
}
